/**
 * App — оболочка виджета «Оплаты».
 *
 * Отвечает за:
 *   - применение темы (data-theme на <html>) из контекста виджета;
 *   - бутстрап авторизации (useAuth): handshake → authenticating → ready | error;
 *   - экраны состояний loading/error;
 *   - монтирование <PaymentsTab> с проброшенными dealId и accountId после успешной авторизации.
 *
 * Реальные компоненты вкладки — следующий этап; здесь PaymentsTab пока заглушка.
 */

import { useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { PaymentsTab } from './features/payments/PaymentsTab';
import { ErrorBoundary } from './features/payments/ErrorBoundary';
import { ApiError } from './lib/apiClient';

export function App(): JSX.Element {
  const { phase, context, error, retry } = useAuth();

  // Тема применяется к корню документа, как только известен контекст.
  useEffect(() => {
    if (context) {
      document.documentElement.dataset.theme = context.theme;
    }
  }, [context]);

  // Авторазмер: сообщаем высоту контента родителю (script.js), чтобы iframe рос под контент
  // (длинная история платежей не «съезжала» и не требовала внутреннего скролла).
  useEffect(() => {
    let parentOrigin = '*';
    try {
      if (document.referrer) parentOrigin = new URL(document.referrer).origin;
    } catch {
      /* ignore */
    }
    let last = 0;
    const post = (): void => {
      try {
        const h = Math.ceil(document.documentElement.scrollHeight);
        if (h && Math.abs(h - last) > 2) {
          last = h;
          window.parent.postMessage({ type: 'PAY_WIDGET_RESIZE', height: h }, parentOrigin);
        }
      } catch {
        /* ignore */
      }
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.documentElement);
    window.addEventListener('load', post);
    return () => {
      ro.disconnect();
      window.removeEventListener('load', post);
    };
  }, []);

  if (phase === 'ready' && context) {
    // accountId для скоупа кэша: доверенный из signedContext, иначе навигационный.
    const accountId = context.signedContext?.amoAccountId ?? context.accountId ?? 'unknown';

    if (!context.dealId) {
      return (
        <main className="pw-app">
          <div className="pw-state">
            <div className="pw-state__title">Откройте карточку сделки</div>
            <p className="pw-state__hint">
              Вкладка «Оплаты» работает внутри карточки сделки amoCRM.
            </p>
          </div>
        </main>
      );
    }

    return (
      <main className="pw-app">
        <ErrorBoundary>
          {/* key по сделке: при SPA-переходе на другую сделку iframe переиспользуется,
              поэтому сбрасываем локальное состояние вкладки (фильтры, режим добавления). */}
          <PaymentsTab key={context.dealId} dealId={context.dealId} accountId={accountId} />
        </ErrorBoundary>
      </main>
    );
  }

  if (phase === 'error') {
    return (
      <main className="pw-app">
        <div className="pw-state" role="alert">
          <div className="pw-state__title">Не удалось авторизоваться</div>
          <p className="pw-state__hint">{describeError(error)}</p>
          <button type="button" className="pw-btn pw-btn--primary" onClick={retry}>
            Повторить
          </button>
        </div>
      </main>
    );
  }

  // handshake | authenticating
  return (
    <main className="pw-app">
      <div className="pw-state" aria-busy="true">
        <span className="pw-spinner" aria-hidden />
        <div className="pw-state__title">
          {phase === 'authenticating' ? 'Авторизация…' : 'Подключение к amoCRM…'}
        </div>
      </div>
    </main>
  );
}

/** Человекочитаемое описание ошибки авторизации (RU). */
function describeError(error: ApiError | Error | null): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return 'Сессия недействительна. Попробуйте переоткрыть карточку сделки.';
      case 403:
        return 'Недостаточно прав для доступа к оплатам.';
      case 404:
        return 'Аккаунт не найден. Возможно, интеграция не установлена.';
      default:
        return error.message || 'Произошла ошибка. Повторите попытку.';
    }
  }
  return error?.message ?? 'Произошла ошибка. Повторите попытку.';
}
